import React, { useState, useCallback, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import {
  Typography, Tabs, Row, Col, Table, Card, Empty, Spin, message, Tag, Drawer, Button,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import ResultsTable from '../components/ResultsTable';
import SidePanel from '../components/SidePanel';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, ResponsiveContainer,
} from 'recharts';
import FilterPanel from '../components/FilterPanel';
import api from '../api';
import { UserContext } from '../App';

const COLORS = ['#4096ff', '#722ed1', '#13c2c2', '#52c41a', '#fa8c16', '#f5222d', '#eb2f96', '#fadb14'];

// ---------------------------------------------------------------------------
// Shared chart / table components
// ---------------------------------------------------------------------------

function HBar({ data, onBarClick }) {
  if (!data?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const top = data.slice(0, 15);
  return (
    <ResponsiveContainer width="100%" height={Math.max(280, top.length * 28)}>
      <BarChart data={top} layout="vertical" margin={{ left: 0, right: 32, top: 4, bottom: 4 }}>
        <XAxis type="number" tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={190} tick={{ fontSize: 11 }} interval={0} />
        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
        <Tooltip formatter={(v, _n, { payload }) => [`${v} (${payload.pct}%)`, 'Calls']} />
        <Bar
          dataKey="calls"
          fill="#4096ff"
          radius={[0, 4, 4, 0]}
          onClick={onBarClick ? (entry) => onBarClick(entry.name) : undefined}
          style={onBarClick ? { cursor: 'pointer' } : undefined}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// The donut shows the top DONUT_MAX - 1 by calls and folds the rest into one
// "Other" slice: a list of hundreds (Topic Categories) made a legend that
// overflowed the page. A blank name shows as "(blank)". The table beside it
// still lists every row.
const DONUT_MAX = 10;
const DONUT_OTHER = 'Other';
function donutSlices(data) {
  const rows = [...data].sort((a, b) => (b.calls || 0) - (a.calls || 0))
    .map((r) => ({ ...r, label: r.name === '' || r.name == null ? '(blank)' : r.name }));
  if (rows.length <= DONUT_MAX) return rows;
  const top = rows.slice(0, DONUT_MAX - 1);
  const rest = rows.slice(DONUT_MAX - 1);
  const total = rows.reduce((t, r) => t + (r.calls || 0), 0);
  const calls = rest.reduce((t, r) => t + (r.calls || 0), 0);
  return [...top, { name: null, label: `${DONUT_OTHER} (${rest.length})`, calls,
    pct: total ? Math.round(calls / total * 1000) / 10 : 0 }];
}

function Donut({ data, onSliceClick }) {
  if (!data?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const slices = donutSlices(data);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={slices}
          dataKey="calls"
          nameKey="label"
          cx="50%" cy="45%"
          innerRadius={55}
          outerRadius={95}
          paddingAngle={2}
          label={({ pct }) => `${pct}%`}
          labelLine={false}
          // "Other" is many rows: not one to drill into.
          onClick={onSliceClick ? (entry) => { if (entry.name != null) onSliceClick(entry.name); } : undefined}
          style={onSliceClick ? { cursor: 'pointer' } : undefined}
        >
          {slices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v, _n, { payload }) => [`${v} (${payload.pct}%)`, payload.label]} />
        <Legend iconSize={10} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Sorters for the by-agent tables. Nulls sort as zero rather than throwing
// the row to one end, since a missing count here means none, not unknown.
const byText = (k) => (a, b) => String(a[k] ?? '').localeCompare(String(b[k] ?? ''));
const byNum  = (k) => (a, b) => (a[k] ?? 0) - (b[k] ?? 0);

// Rank is a property of the whole list, not of the page being looked at, so
// it is resolved by name against the full data set. The row index Ant hands
// the renderer is the index within the current page, which restarted the
// numbering at 1 on page 2.
const statCols = (rankByName) => [
  { title: '#', key: 'rank', render: (_, r) => rankByName.get(r.name), width: 36 },
  { title: 'Name', dataIndex: 'name', ellipsis: true },
  { title: 'Calls', dataIndex: 'calls', align: 'right', width: 72 },
  { title: '%', dataIndex: 'pct', align: 'right', width: 60, render: (v) => `${v}%` },
];

function StatTable({ data, onRowClick, selectedRow, onDrillDown }) {
  if (!data?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  // When a row already has a click action (e.g. Reasons correlation), show an eye
  // icon for drill-down instead of making the row itself open the drawer.
  const hasEyeCol = !!(onDrillDown && onRowClick);
  // rowKey is name, so names are unique and safe to rank by.
  const rankByName = new Map(data.map((r, i) => [r.name, i + 1]));
  const baseCols = statCols(rankByName);
  const cols = hasEyeCol
    ? [...baseCols, {
        title: '',
        key: 'view',
        width: 36,
        render: (_, record) => (
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            title="View top 100 records"
            onClick={(e) => { e.stopPropagation(); onDrillDown(record.name); }}
          />
        ),
      }]
    : baseCols;

  // When no other row action exists, the row click itself opens drill-down.
  const effectiveRowClick = onRowClick ?? onDrillDown;

  // selectedRow takes a name or a list of names, so a table can carry a
  // multi-value filter as well as a single click.
  const isSelected = Array.isArray(selectedRow)
    ? (n) => selectedRow.includes(n)
    : (n) => selectedRow === n;

  return (
    <Table
      dataSource={data}
      columns={cols}
      rowKey="name"
      size="small"
      pagination={{ defaultPageSize: 20, size: 'small', showSizeChanger: false, hideOnSinglePage: true }}
      onRow={effectiveRowClick ? (record) => ({
        onClick: () => effectiveRowClick(record.name),
        style: {
          cursor: 'pointer',
          background: isSelected(record.name) ? '#e6f4ff' : undefined,
          fontWeight: isSelected(record.name) ? 600 : undefined,
        },
      }) : undefined}
      summary={() => {
        const total = data.reduce((s, r) => s + r.calls, 0);
        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}><strong>Grand total</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right"><strong>{total.toLocaleString()}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={3} colSpan={hasEyeCol ? 2 : 1} />
          </Table.Summary.Row>
        );
      }}
    />
  );
}

// A ranked list. chartType 'bar' or 'donut' puts a chart beside the table;
// 'none', the default, leaves the table on its own across the full width.
//
// The charts were drawn from exactly the same rows as the table sitting
// underneath or beside them, and said less: a donut past about six slices
// has colliding labels and indistinguishable wedges, and a bar chart of a
// ranked list is a ranked list with the numbers taken off.
function RankedSection({ title, data, chartType = 'none', filterKey, onDrillDown }) {
  const handleClick = onDrillDown && filterKey ? (name) => onDrillDown(filterKey, name) : undefined;
  const table = <StatTable data={data} onDrillDown={handleClick} />;
  return (
    <Card title={title} size="small" style={{ marginBottom: 16 }}>
      {chartType === 'none' ? table : (
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            {chartType === 'bar'
              ? <HBar data={data} onBarClick={handleClick} />
              : <Donut data={data} onSliceClick={handleClick} />}
          </Col>
          <Col xs={24} lg={12}>{table}</Col>
        </Row>
      )}
    </Card>
  );
}

// A short breakdown. Same contract as RankedSection, but a chart, when one is
// asked for, sits above the table rather than beside it.
function SmallSection({ title, data, chartType = 'none', filterKey, onDrillDown }) {
  const handleClick = onDrillDown && filterKey ? (name) => onDrillDown(filterKey, name) : undefined;
  const chart = chartType === 'none' ? null
    : chartType === 'donut' ? <Donut data={data} onSliceClick={handleClick} />
    : <HBar data={data} onBarClick={handleClick} />;
  return (
    <Card title={title} size="small">
      {chart}
      <div style={{ marginTop: chart ? 8 : 0 }}>
        <StatTable data={data} onDrillDown={handleClick} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Daily trend charts
// ---------------------------------------------------------------------------

const fmtDay = (s) => (s ? s.slice(5).replace('-', '/') : s); // "YYYY-MM-DD" → "MM/DD"

// Multi-line chart: flat [{day, name, calls}] → pivot top-N names into one line each
function MultiLineTrendChart({ title, data, topN = 5 }) {
  if (!data?.length) return null;

  // Rank names by total calls across all days
  const totals = {};
  for (const row of data) {
    totals[row.name] = (totals[row.name] || 0) + row.calls;
  }
  const topNames = Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([name]) => name);
  const nameSet = new Set(topNames);

  // Pivot rows into {day, [name]: calls}
  const byDay = {};
  for (const row of data) {
    if (!nameSet.has(row.name)) continue;
    if (!byDay[row.day]) byDay[row.day] = { day: row.day };
    byDay[row.day][row.name] = row.calls;
  }
  const chartData = Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));

  return (
    <Card title={`Top ${topN} ${title}`} size="small" style={{ marginTop: 16 }}>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
          <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip labelFormatter={(l) => `Date: ${l}`} />
          <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          {topNames.map((name, i) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              dot={chartData.length <= 20}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

function DailyTrendChart({ title, data, bars, lines }) {
  if (!data?.length) return null;
  const isRate = lines?.length > 0;
  return (
    <Card title={title} size="small" style={{ marginBottom: 16 }}>
      <ResponsiveContainer width="100%" height={180}>
        {isRate ? (
          <LineChart data={data} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip formatter={(v) => `${v}%`} labelFormatter={(l) => `Date: ${l}`} />
            <Legend iconSize={10} />
            {lines.map((l, i) => (
              <Line key={l.key} type="monotone" dataKey={l.key} name={l.label}
                stroke={COLORS[i]} strokeWidth={2} dot={data.length <= 20} />
            ))}
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip labelFormatter={(l) => `Date: ${l}`} />
            {bars.map((b, i) => (
              <Bar key={b.key} dataKey={b.key} name={b.label} fill={COLORS[i]} radius={[2, 2, 0, 0]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Drill-down drawer — shows top 100 call records for the clicked segment
// ---------------------------------------------------------------------------

function DrillDownDrawer({ drillDown, onClose }) {
  const [records, setRecords]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selectedId, setSelectedId]   = useState(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  useEffect(() => {
    if (!drillDown) return;
    setLoading(true);
    const params = { ...drillDown.filters, [drillDown.filterKey]: drillDown.filterValue, page_size: 100 };
    api.get('/api/calls', { params })
      .then((res) => setRecords(res.data.data || []))
      .catch(() => message.error('Failed to load records.'))
      .finally(() => setLoading(false));
  }, [drillDown]);

  const handleRowClick = (record) => {
    setSelectedId(record.ContactID);
    setSidePanelOpen(true);
  };

  const handleFlagToggle = (contactId, nowFlagged) => {
    setRecords((prev) =>
      prev.map((r) =>
        r.ContactID === contactId
          ? { ...r, UserHasFlagged: nowFlagged ? 1 : 0, FlagCount: nowFlagged ? r.FlagCount + 1 : Math.max(0, r.FlagCount - 1) }
          : r
      )
    );
  };

  return (
    <>
      <Drawer
        title={`Top 100 — ${drillDown?.filterValue ?? ''}`}
        open={!!drillDown}
        onClose={onClose}
        width={1100}
        destroyOnClose
      >
        <ResultsTable
          data={records}
          loading={loading}
          onRowClick={handleRowClick}
          onFlagToggle={handleFlagToggle}
        />
      </Drawer>

      <SidePanel
        contactId={selectedId}
        open={sidePanelOpen}
        onClose={() => setSidePanelOpen(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab content
// ---------------------------------------------------------------------------

function TopicsTab({ data, onDrillDown }) {
  return (
    <>
      <RankedSection
        title="Topics  (top 15 shown in chart)"
        data={data?.topics}
        filterKey="topic"
        onDrillDown={onDrillDown}
      />
      <MultiLineTrendChart
        title="Topics by Day"
        data={data?.topics_by_day}
      />
    </>
  );
}

function TopicCategoriesTab({ data, onDrillDown }) {
  return (
    <>
      <RankedSection
        title="Topic Categories"
        data={data?.topic_categories}
        chartType="donut"
        filterKey="topic_category"
        onDrillDown={onDrillDown}
      />
      <MultiLineTrendChart
        title="Topic Categories by Day"
        data={data?.topic_cats_by_day}
      />
    </>
  );
}

// Exported so the Campaign Summary renders the same component rather than a
// second copy of these two tables. It takes everything it needs as props, so
// nothing about the page around it comes with it.
export function ReasonsOutcomesTab({ data, filters, onDrillDown }) {
  const [selectedReason, setSelectedReason]     = useState(null);
  const [filteredOutcomes, setFilteredOutcomes] = useState(null);
  const [loadingOutcomes, setLoadingOutcomes]   = useState(false);

  useEffect(() => {
    setSelectedReason(null);
    setFilteredOutcomes(null);
  }, [data]);

  const handleReasonClick = (reason) => {
    if (selectedReason === reason) {
      setSelectedReason(null);
      setFilteredOutcomes(null);
      return;
    }
    setSelectedReason(reason);
    setLoadingOutcomes(true);
    api.get('/api/analytics', { params: { ...filters, reason } })
      .then((res) => setFilteredOutcomes(res.data.outcomes))
      .catch(() => message.error('Failed to load correlated outcomes.'))
      .finally(() => setLoadingOutcomes(false));
  };

  const clearSelection = () => {
    setSelectedReason(null);
    setFilteredOutcomes(null);
  };

  const outcomesData  = filteredOutcomes  ?? data?.outcomes;
  const outcomesTitle = selectedReason
    ? <>Outcomes <Tag color="blue" closable onClose={clearSelection}>{selectedReason}</Tag></>
    : 'Outcomes  (click a reason to filter)';

  const reasonDrillDown  = onDrillDown ? (name) => onDrillDown('reason', name)  : undefined;
  const outcomeDrillDown = onDrillDown ? (name) => onDrillDown('outcome', name) : undefined;

  return (
    <>
      <Row gutter={16}>
      <Col xs={24} xl={12}>
        <Card title="Reasons  (click a row to filter outcomes)" size="small" style={{ marginBottom: 16 }}>
          <StatTable
            data={data?.reasons}
            onRowClick={handleReasonClick}
            selectedRow={selectedReason}
            onDrillDown={reasonDrillDown}
          />
        </Card>
      </Col>

      <Col xs={24} xl={12}>
        <Card title={outcomesTitle} size="small" style={{ marginBottom: 16 }}>
          {loadingOutcomes ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Spin />
            </div>
          ) : (
            <StatTable data={outcomesData} onDrillDown={outcomeDrillDown} />
          )}
        </Card>
      </Col>
      </Row>
      <MultiLineTrendChart
        title="Reasons by Day"
        data={data?.reasons_by_day}
      />
      <MultiLineTrendChart
        title="Outcomes by Day"
        data={data?.outcomes_by_day}
      />
    </>
  );
}

// The circumstances the upsell numbers are actually about: a call where an
// upsell should have been considered. The database calls that "other" today
// and is being renamed to "valid" to say so plainly. Both names are listed so
// the default holds before, during and after that rename -- whichever exists
// in the returned data is what gets selected, and a period where both exist
// selects both.
const UPSELL_FOCUS = ['other', 'valid'];

function UpsellingTab({ data, filters, onDrillDown }) {
  const [selectedCircumstances, setSelectedCircumstances] = useState([]);
  const [attempted, setAttempted]                         = useState(null);
  const [circumstanceData, setCircumstanceData]           = useState(null);
  const [filteredData, setFilteredData]                   = useState(null);
  const [loadingFilter, setLoadingFilter]                 = useState(false);

  // One path for the defaults and for every click, so the two cannot diverge.
  // Setting a selection without also refetching would leave the tab showing
  // unfiltered numbers underneath a filter tag.
  //
  // Two responses, not one: the circumstance-only set is what the Attempted
  // card reads, so choosing a value there does not collapse that card to the
  // single row just selected. When nothing is chosen for Attempted the second
  // request is skipped and both read the same response.
  const applyFilters = useCallback(async (names, att) => {
    setSelectedCircumstances(names);
    setAttempted(att || null);

    const params = { ...filters };
    // _in_col splits on commas server-side, so several values filter as an IN.
    if (names.length) params.upsell_circumstance = names.join(',');

    if (!names.length && !att) {
      setCircumstanceData(null);
      setFilteredData(null);
      return;
    }

    setLoadingFilter(true);
    try {
      const byCirc = names.length ? (await api.get('/api/analytics', { params })).data : null;
      setCircumstanceData(byCirc);
      setFilteredData(att
        ? (await api.get('/api/analytics', { params: { ...params, upselling_attempted: att } })).data
        : byCirc);
    } catch {
      message.error('Failed to load filtered upsell data.');
    } finally {
      setLoadingFilter(false);
    }
  }, [filters]);

  const applyCircumstances = useCallback((names) => applyFilters(names, null), [applyFilters]);

  // Matched case-insensitively against what came back rather than assumed, so
  // a name this page has never seen simply selects nothing instead of
  // filtering everything away.
  useEffect(() => {
    applyCircumstances(
      (data?.upsell_circumstance || [])
        .map((r) => r.name)
        .filter((n) => UPSELL_FOCUS.includes(String(n).trim().toLowerCase()))
    );
  }, [data, applyCircumstances]);

  const handleCircumstanceClick = (circumstance) => {
    applyFilters(selectedCircumstances.includes(circumstance)
      ? selectedCircumstances.filter((n) => n !== circumstance)
      : [...selectedCircumstances, circumstance], attempted);
  };

  const toggleAttempted = (name) =>
    applyFilters(selectedCircumstances, attempted === name ? null : name);

  // All other widgets use filtered data when a circumstance is selected
  const display = filteredData ?? data;
  const circumstanceDrillDown = onDrillDown ? (name) => onDrillDown('upsell_circumstance', name) : undefined;
  const attemptedDrillDown    = onDrillDown ? (name) => onDrillDown('upselling_attempted', name) : undefined;

  const attemptedTitle = attempted
    ? <>Upsell Attempted <Tag color="blue" closable onClose={() => toggleAttempted(attempted)}>{attempted}</Tag></>
    : 'Upsell Attempted  (click to filter)';

  const circumstanceTitle = selectedCircumstances.length
    ? (
      <>
        Upsell Circumstance{' '}
        {selectedCircumstances.map((c) => (
          <Tag key={c} color="blue" closable onClose={() => handleCircumstanceClick(c)}>{c}</Tag>
        ))}
      </>
    )
    : 'Upsell Circumstance  (click to filter)';

  const agentCols = [
    { title: 'Agent',      dataIndex: 'agent',     ellipsis: true, sorter: byText('agent') },
    { title: 'AI Records', dataIndex: 'total',     align: 'right', width: 110, sorter: byNum('total') },
    { title: 'Attempted',  dataIndex: 'attempted', align: 'right', width: 110, sorter: byNum('attempted') },
    { title: 'Rate %',     dataIndex: 'rate',      align: 'right', width: 95,  sorter: byNum('rate'), render: (v) => `${v}%` },
  ];

  return (
    <>
      <DailyTrendChart
        title="Upsell Attempt Rate by Day"
        data={display?.upsell_by_day}
        lines={[{ key: 'rate', label: 'Attempted %' }]}
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card title={circumstanceTitle} size="small">
            {loadingFilter ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <StatTable
                data={data?.upsell_circumstance}
                selectedRow={selectedCircumstances}
                onRowClick={handleCircumstanceClick}
                onDrillDown={circumstanceDrillDown}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          {/* Both handlers: a row click sets the filter, and the eye opens the
              sample calls behind that value. StatTable shows the eye column
              only when it has somewhere else to send a plain row click. */}
          <Card title={attemptedTitle} size="small">
            {loadingFilter ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <StatTable
                data={(circumstanceData ?? data)?.upsell_attempted}
                selectedRow={attempted}
                onRowClick={toggleAttempted}
                onDrillDown={attemptedDrillDown}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <SmallSection title="Upsell — Has Policy" data={display?.upsell_has_policy} filterKey="upsell_has_policy"   onDrillDown={onDrillDown} />
        </Col>
      </Row>
      <Card title="Upselling by Agent" size="small">
        <Table
          dataSource={display?.upsell_by_agent || []}
          columns={agentCols}
          rowKey="agent"
          size="small"
          pagination={{ defaultPageSize: 20, size: 'small', hideOnSinglePage: true }}
          onRow={onDrillDown ? (record) => ({
            onClick: () => onDrillDown('agent_name', record.agent),
            style: { cursor: 'pointer' },
          }) : undefined}
        />
      </Card>
    </>
  );
}

// The save numbers only mean anything on calls where a save was applicable at
// all: the other 97.6% are 'not applicable' rows that flatten every rate.
// Matched against what came back rather than assumed, so a renamed value
// selects nothing instead of filtering the tab down to nothing.
const SAVE_APPLICABLE_FOCUS = 'yes';

function SaveAttemptsTab({ data, filters, onDrillDown }) {
  const [applicable, setApplicable]       = useState(null);
  const [filteredData, setFilteredData]   = useState(null);
  const [loadingFilter, setLoadingFilter] = useState(false);

  // One path for the default and for a click, so the two cannot diverge.
  const applyApplicable = useCallback((name) => {
    setApplicable(name || null);
    if (!name) {
      setFilteredData(null);
      return;
    }
    setLoadingFilter(true);
    api.get('/api/analytics', { params: { ...filters, save_attempt_applicable: name } })
      .then((res) => setFilteredData(res.data))
      .catch(() => message.error('Failed to load filtered save attempt data.'))
      .finally(() => setLoadingFilter(false));
  }, [filters]);

  useEffect(() => {
    applyApplicable(
      (data?.save_applicable || [])
        .map((r) => r.name)
        .find((n) => String(n).trim().toLowerCase() === SAVE_APPLICABLE_FOCUS) ?? null
    );
  }, [data, applyApplicable]);

  const toggleApplicable = (name) => applyApplicable(applicable === name ? null : name);

  // Everything except the Applicable card itself reads the filtered set. That
  // card keeps the full list so the filter can be changed or cleared from it.
  const display = filteredData ?? data;

  const applicableTitle = applicable
    ? <>Save Attempt Applicable <Tag color="blue" closable onClose={() => applyApplicable(null)}>{applicable}</Tag></>
    : 'Save Attempt Applicable  (click to filter)';

  const agentCols = [
    { title: 'Agent',      dataIndex: 'agent',      ellipsis: true, sorter: byText('agent') },
    { title: 'AI Records', dataIndex: 'total',      align: 'right', width: 110, sorter: byNum('total') },
    { title: 'Applicable', dataIndex: 'applicable', align: 'right', width: 110, sorter: byNum('applicable') },
    { title: 'Made',       dataIndex: 'made',       align: 'right', width: 95,  sorter: byNum('made') },
  ];
  return (
    <>
      <DailyTrendChart
        title="Save Attempt Rates by Day"
        data={display?.save_by_day}
        lines={[
          { key: 'applicable_rate', label: 'Applicable %' },
          { key: 'made_rate',       label: 'Made %' },
        ]}
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          {/* No onDrillDown: clicking a row here sets the filter for the tab,
              it does not open the record list. */}
          <Card title={applicableTitle} size="small">
            {loadingFilter ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <StatTable
                data={data?.save_applicable}
                selectedRow={applicable}
                onRowClick={toggleApplicable}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <SmallSection title="Save Attempt Made"       data={display?.save_made}    filterKey="save_attempt_made"        onDrillDown={onDrillDown} />
        </Col>
        <Col xs={24} md={8}>
          <SmallSection title="Save Attempt Outcome"    data={display?.save_outcome} filterKey="save_attempt_outcome"     onDrillDown={onDrillDown} />
        </Col>
      </Row>
      <Card title="Save Attempts by Agent" size="small">
        <Table
          dataSource={display?.save_by_agent || []}
          columns={agentCols}
          rowKey="agent"
          size="small"
          pagination={{ defaultPageSize: 20, size: 'small', hideOnSinglePage: true }}
          onRow={onDrillDown ? (record) => ({
            onClick: () => onDrillDown('agent_name', record.agent),
            style: { cursor: 'pointer' },
          }) : undefined}
        />
      </Card>
    </>
  );
}

function AudioIssuesTab({ data, onDrillDown }) {
  const agentCols = [
    { title: 'Agent',      dataIndex: 'agent',  ellipsis: true, sorter: byText('agent') },
    { title: 'AI Records', dataIndex: 'total',  align: 'right', width: 110, sorter: byNum('total') },
    { title: 'Issues',     dataIndex: 'issues', align: 'right', width: 95,  sorter: byNum('issues') },
    { title: 'Rate %',     dataIndex: 'rate',   align: 'right', width: 95,  sorter: byNum('rate'), render: (v) => `${v}%` },
  ];
  return (
    <>
      <DailyTrendChart
        title="Audio Issue Rate by Day"
        data={data?.audio_by_day}
        lines={[{ key: 'rate', label: 'Issue rate %' }]}
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <SmallSection title="Audio Issues"         data={data?.audio_issues} filterKey="audio_issues"       onDrillDown={onDrillDown} />
        </Col>
        <Col xs={24} md={14}>
          <SmallSection title="Audio Issues — Party" data={data?.audio_party}  filterKey="audio_issues_party" onDrillDown={onDrillDown} />
        </Col>
      </Row>
      <Card title="Audio Issues by Agent" size="small">
        <Table
          dataSource={data?.audio_by_agent || []}
          columns={agentCols}
          rowKey="agent"
          size="small"
          pagination={{ defaultPageSize: 20, size: 'small', hideOnSinglePage: true }}
          onRow={onDrillDown ? (record) => ({
            onClick: () => onDrillDown('agent_name', record.agent),
            style: { cursor: 'pointer' },
          }) : undefined}
        />
      </Card>
    </>
  );
}

function MissedOpportunitiesTab({ data, filters, onDrillDown }) {
  const [outcome, setOutcome]             = useState(null);
  const [filteredData, setFilteredData]   = useState(null);
  const [loadingFilter, setLoadingFilter] = useState(false);

  // A fresh search clears the selection: the outcome that was chosen may not
  // exist in the new result set.
  useEffect(() => {
    setOutcome(null);
    setFilteredData(null);
  }, [data]);

  const applyOutcome = useCallback((name) => {
    setOutcome(name || null);
    if (!name) {
      setFilteredData(null);
      return;
    }
    setLoadingFilter(true);
    api.get('/api/analytics', { params: { ...filters, oppt_outcome: name } })
      .then((res) => setFilteredData(res.data))
      .catch(() => message.error('Failed to load filtered opportunity data.'))
      .finally(() => setLoadingFilter(false));
  }, [filters]);

  const toggleOutcome = (name) => applyOutcome(outcome === name ? null : name);

  // Everything but the Outcome card reads the filtered set. That card keeps
  // the full list, since it is what the filter is chosen from.
  const display = filteredData ?? data;

  const outcomeTitle = outcome
    ? <>By Outcome <Tag color="blue" closable onClose={() => applyOutcome(null)}>{outcome}</Tag></>
    : 'By Outcome  (click to filter)';

  const agentCols = [
    { title: 'Agent',         dataIndex: 'agent',    ellipsis: true, sorter: byText('agent') },
    { title: 'Opportunities', dataIndex: 'total',    align: 'right', width: 135, sorter: byNum('total') },
    { title: 'Contacts',      dataIndex: 'contacts', align: 'right', width: 115, sorter: byNum('contacts') },
  ];
  return (
    <>
      <DailyTrendChart
        title="Opportunities by Day"
        data={display?.oppt_by_day}
        bars={[
          { key: 'opportunities', label: 'Opportunities' },
          { key: 'contacts',      label: 'Contacts' },
        ]}
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <SmallSection title="By Type"    data={display?.oppt_types}   filterKey="oppt_type"   onDrillDown={onDrillDown} />
        </Col>
        <Col xs={24} md={8}>
          {/* No onDrillDown: a click here filters the tab rather than opening
              the record list. */}
          <Card title={outcomeTitle} size="small">
            {loadingFilter ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <StatTable
                data={data?.oppt_outcomes}
                selectedRow={outcome}
                onRowClick={toggleOutcome}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <SmallSection title="By Reason"  data={display?.oppt_reasons} filterKey="oppt_reason" onDrillDown={onDrillDown} />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={12}>
          <SmallSection  title="By State"       data={display?.oppt_by_state}      filterKey="state"            onDrillDown={onDrillDown} />
        </Col>
        <Col xs={24} xl={12}>
          <RankedSection title="By Opportunity" data={display?.oppt_opportunities} filterKey="oppt_opportunity" onDrillDown={onDrillDown} />
        </Col>
      </Row>
      <Card title="Missed Opportunities by Agent" size="small">
        <Table
          dataSource={display?.oppt_by_agent || []}
          columns={agentCols}
          rowKey="agent"
          size="small"
          pagination={{ defaultPageSize: 20, size: 'small', hideOnSinglePage: true }}
          onRow={onDrillDown ? (record) => ({
            onClick: () => onDrillDown('agent_name', record.agent),
            style: { cursor: 'pointer' },
          }) : undefined}
        />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Is Resolved tab
// ---------------------------------------------------------------------------

const RESOLVED_COLS = [
  { title: '#',               render: (_, __, i) => i + 1,  width: 36 },
  { title: 'Name',            dataIndex: 'name',             ellipsis: true },
  { title: 'Resolved — Yes',  dataIndex: 'yes',  align: 'right', width: 130 },
  { title: 'Resolved — No',   dataIndex: 'no',   align: 'right', width: 120 },
  { title: 'Total',           dataIndex: 'total', align: 'right', width: 80 },
];

const AGENT_RESOLVED_COLS = [
  { title: '#',               render: (_, __, i) => i + 1,  width: 36 },
  { title: 'Agent',           dataIndex: 'name',             ellipsis: true },
  { title: 'Resolved — Yes',  dataIndex: 'yes',  align: 'right', width: 130 },
  { title: 'Resolved — No',   dataIndex: 'no',   align: 'right', width: 120 },
  { title: 'Total',           dataIndex: 'total', align: 'right', width: 80 },
];

function IsResolvedTab({ data, filters }) {
  const [selection, setSelection]           = useState(null); // { filterKey, value }
  const [filteredAgents, setFilteredAgents] = useState(null);
  const [loadingAgents, setLoadingAgents]   = useState(false);

  useEffect(() => {
    setSelection(null);
    setFilteredAgents(null);
  }, [data]);

  const handleSelect = (filterKey, value) => {
    if (selection?.filterKey === filterKey && selection?.value === value) {
      setSelection(null);
      setFilteredAgents(null);
      return;
    }
    setSelection({ filterKey, value });
    setLoadingAgents(true);
    api.get('/api/analytics', { params: { ...filters, [filterKey]: value } })
      .then((res) => setFilteredAgents(res.data.agent_by_resolved))
      .catch(() => message.error('Failed to load filtered agent data.'))
      .finally(() => setLoadingAgents(false));
  };

  const clearSelection = () => {
    setSelection(null);
    setFilteredAgents(null);
  };

  const agentData = filteredAgents ?? data?.agent_by_resolved;

  const resolvedSummary = (rows) => {
    const yes = rows.reduce((s, r) => s + (r.yes || 0), 0);
    const no  = rows.reduce((s, r) => s + (r.no  || 0), 0);
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right"><strong>{yes.toLocaleString()}</strong></Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right"><strong>{no.toLocaleString()}</strong></Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right"><strong>{(yes + no).toLocaleString()}</strong></Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const makeTable = (dataSource, filterKey) => (
    <Table
      dataSource={dataSource || []}
      columns={RESOLVED_COLS}
      rowKey="name"
      size="small"
      pagination={{ defaultPageSize: 25, size: 'small', hideOnSinglePage: true }}
      summary={resolvedSummary}
      onRow={(record) => ({
        onClick: () => handleSelect(filterKey, record.name),
        style: {
          cursor: 'pointer',
          background: selection?.filterKey === filterKey && selection?.value === record.name ? '#e6f4ff' : undefined,
          fontWeight: selection?.filterKey === filterKey && selection?.value === record.name ? 600 : undefined,
        },
      })}
    />
  );

  const agentTitle = selection
    ? <>By Agent <Tag color="blue" closable onClose={clearSelection}>{selection.value}</Tag></>
    : 'By Agent  (click a row above to filter)';

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={8}>
          <Card title="Topics" size="small">
            {makeTable(data?.topics_by_resolved, 'topic')}
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card title="Reasons" size="small">
            {makeTable(data?.reasons_by_resolved, 'reason')}
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card title="Outcomes" size="small">
            {makeTable(data?.outcomes_by_resolved, 'outcome')}
          </Card>
        </Col>
      </Row>
      <Card title={agentTitle} size="small">
        {loadingAgents ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <Table
            dataSource={agentData || []}
            columns={AGENT_RESOLVED_COLS}
            rowKey="name"
            size="small"
            pagination={{ defaultPageSize: 25, size: 'small', hideOnSinglePage: true }}
            summary={resolvedSummary}
          />
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const buildTabs = (data, filters, onDrillDown) => [
  { key: 'reasons',   label: 'Reasons & Outcomes',   children: <ReasonsOutcomesTab data={data} filters={filters} onDrillDown={onDrillDown} /> },
  { key: 'topics',    label: 'Topics',               children: <TopicsTab data={data} onDrillDown={onDrillDown} /> },
  { key: 'topic_cat', label: 'Topic Categories',     children: <TopicCategoriesTab data={data} onDrillDown={onDrillDown} /> },
  { key: 'upsell',    label: 'Upselling',            children: <UpsellingTab data={data} filters={filters} onDrillDown={onDrillDown} /> },
  { key: 'save',      label: 'Save Attempts',        children: <SaveAttemptsTab data={data} filters={filters} onDrillDown={onDrillDown} /> },
  { key: 'audio',     label: 'Audio Issues',         children: <AudioIssuesTab data={data} onDrillDown={onDrillDown} /> },
  { key: 'oppt',      label: 'Missed Opportunities', children: <MissedOpportunitiesTab data={data} filters={filters} onDrillDown={onDrillDown} /> },
  { key: 'resolved',  label: 'Is Resolved',          children: <IsResolvedTab data={data} filters={filters} /> },
];

export default function AnalyticsPage() {
  const currentUser = useContext(UserContext);
  const allowedTabs = currentUser?.AnalyticsTabs || [];

  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [drillDown, setDrillDown]         = useState(null);

  const handleSearch = useCallback((filters) => {
    setActiveFilters(filters);
    setLoading(true);
    api.get('/api/analytics', { params: filters })
      .then((res) => setData(res.data))
      .catch(() => message.error('Failed to load analytics. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // Capture activeFilters at click time so the drawer shows consistent results
  // even if the user re-runs analytics while the drawer is open.
  const openDrillDown = useCallback((filterKey, filterValue) => {
    setDrillDown({ filterKey, filterValue, filters: activeFilters });
  }, [activeFilters]);

  const visibleTabs = buildTabs(data, activeFilters, openDrillDown)
    .filter((t) => allowedTabs.length === 0 || allowedTabs.includes(t.key));

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Analytics
      </Typography.Title>

      <FilterPanel onSearch={handleSearch} loading={loading} buttonLabel="Run Analytics" requireCampaign showCalledFilters defaultDateRange={[dayjs().subtract(1, 'month').startOf('month'), dayjs()]} />

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 60 }}>
          <Spin size="large" tip="Running analytics…" />
        </div>
      )}

      {!loading && data && (
        <Tabs
          items={visibleTabs}
          defaultActiveKey={visibleTabs[0]?.key}
        />
      )}

      {!loading && !data && (
        <Empty
          description="Set your filters above and click Run Analytics"
          style={{ marginTop: 60 }}
        />
      )}

      <DrillDownDrawer
        drillDown={drillDown}
        onClose={() => setDrillDown(null)}
      />
    </>
  );
}
